import { useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../Firebase.js";
import "../App.css";
import {
    FaMusic,
    FaList,
    FaGamepad,
    FaPlay,
    FaArrowRight,
} from "react-icons/fa";

// Custom hook for count-up animation
function useCountUp(targetValue, duration = 2000, startAnimation = false) {
    const [count, setCount] = useState(0);
    const requestRef = useRef();
    const startTimeRef = useRef();

    useEffect(() => {
        if (!startAnimation || targetValue === 0) {
            setCount(targetValue);
            return;
        }

        const animate = (currentTime) => {
            if (!startTimeRef.current) {
                startTimeRef.current = currentTime;
            }

            const elapsed = currentTime - startTimeRef.current;
            const progress = Math.min(elapsed / duration, 1);

            // Easing function for smooth animation (ease-out)
            const easeOut = 1 - Math.pow(1 - progress, 3);
            const currentCount = Math.floor(easeOut * targetValue);

            setCount(currentCount);

            if (progress < 1) {
                requestRef.current = requestAnimationFrame(animate);
            } else {
                setCount(targetValue);
            }
        };

        requestRef.current = requestAnimationFrame(animate);

        return () => {
            if (requestRef.current) {
                cancelAnimationFrame(requestRef.current);
            }
        };
    }, [targetValue, duration, startAnimation]);

    return count;
}

function Information() {
    const navigate = useNavigate();
    const [stats, setStats] = useState({
        songs: 0,
        playlists: 0,
        sessions: 0,
        loading: true,
    });

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const [songsSnapshot, playlistsSnapshot, sessionsSnapshot] =
                    await Promise.all([
                        getDocs(collection(db, "songs")),
                        getDocs(collection(db, "playlists")),
                        getDocs(collection(db, "sessions")),
                    ]);

                setStats({
                    songs: songsSnapshot.size,
                    playlists: playlistsSnapshot.size,
                    sessions: sessionsSnapshot.size,
                    loading: false,
                });
            } catch (error) {
                console.error("Error fetching stats:", error);
                setStats((prev) => ({ ...prev, loading: false }));
            }
        };

        fetchStats();
    }, []);

    // Use count-up animation for each stat
    const animatedSongs = useCountUp(stats.songs, 2000, !stats.loading);
    const animatedPlaylists = useCountUp(stats.playlists, 2000, !stats.loading);
    const animatedSessions = useCountUp(stats.sessions, 2000, !stats.loading);

    return (
        <div
            style={{
                minHeight: "100vh",
                backgroundColor: "#0f1624",
                color: "white",
                overflowY: "auto",
            }}
        >
            {/* Hero Section */}
            <div
                style={{
                    position: "relative",
                    background:
                        "linear-gradient(to bottom, #161f33 0%, #0f1624 50%, #2a3854 100%)",
                    padding: "5vh 5vw 12vh 5vw",
                    textAlign: "center",
                }}
            >
                {/* Bottom curved border - gradient end color */}
                <svg
                    style={{
                        position: "absolute",
                        bottom: "-80px",
                        left: 0,
                        width: "100%",
                        height: "80px",
                        display: "block",
                    }}
                    xmlns="http://www.w3.org/2000/svg"
                    xmlnsXlink="http://www.w3.org/1999/xlink"
                    viewBox="0 0 1200 120"
                    preserveAspectRatio="none"
                >
                    <defs>
                        <filter id="glow-hero">
                            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                            <feMerge>
                                <feMergeNode in="coloredBlur"/>
                                <feMergeNode in="SourceGraphic"/>
                            </feMerge>
                        </filter>
                    </defs>
                    <path
                        d="M0,60 C300,130 600,0 900,60 C1050,90 1150,20 1200,60"
                        fill="none"
                        stroke="#2a3854"
                        strokeWidth="2.5"
                        filter="url(#glow-hero)"
                    />
                </svg>
                <h1
                    style={{
                        fontSize: "clamp(2rem, 4vw, 3.5rem)",
                        fontWeight: "bold",
                        marginBottom: "0.5vh",
                        background:
                            "linear-gradient(135deg, #4db193 0%, #3a9a7a 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                    }}
                >
                    Velkommen til Beat 4 beat
                </h1>
                <p
                    style={{
                        fontSize: "clamp(1rem, 2vw, 1.4rem)",
                        color: "#d1d5db",
                        marginTop: "1.5vh",
                        maxWidth: "800px",
                        margin: "1.5vh auto 0",
                        lineHeight: 1.5,
                        fontWeight: 500,
                    }}
                >
                    🎤 Varm opp sangstemmen, fyll glassene og opplev deres helt
                    egen versjon av beat for beat!
                </p>
                <p
                    style={{
                        fontSize: "clamp(0.85rem, 1.6vw, 1rem)",
                        color: "#9ca3af",
                        marginTop: "1vh",
                        maxWidth: "800px",
                        margin: "1vh auto 0",
                        lineHeight: 1.5,
                        fontStyle: "italic",
                    }}
                >
                    For de som har sett Beat for Beat på TV, er det ca samme
                    regler her.
                </p>
                <button
                    onClick={() => navigate("/setup")}
                    style={{
                        backgroundColor: "#4db193",
                        color: "white",
                        border: "none",
                        borderRadius: "0.5vh",
                        padding: "1.2vh 2.5vw",
                        fontSize: "clamp(0.95rem, 1.6vw, 1.1rem)",
                        fontWeight: "bold",
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.8vh",
                        transition: "all 0.3s ease",
                        boxShadow: "0 4px 6px rgba(77, 177, 147, 0.3)",
                        marginTop: "6vh",
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#3d9a7a";
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow =
                            "0 6px 12px rgba(77, 177, 147, 0.4)";
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#4db193";
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow =
                            "0 4px 6px rgba(77, 177, 147, 0.3)";
                    }}
                >
                    <FaPlay />
                    Kom i gang
                    <FaArrowRight />
                </button>
            </div>

            {/* Statistics Section */}
            <div
                style={{
                    position: "relative",
                    padding: "4vh 5vw",
                    display: "flex",
                    justifyContent: "center",
                    gap: "3vw",
                    flexWrap: "wrap",
                    backgroundColor: "#161f33",
                    marginTop: "-2px",
                }}
            >
                {/* Middle curved border - blend between gradient end and section color */}
                <svg
                    style={{
                        position: "absolute",
                        top: "-80px",
                        left: 0,
                        width: "100%",
                        height: "80px",
                        display: "block",
                    }}
                    xmlns="http://www.w3.org/2000/svg"
                    xmlnsXlink="http://www.w3.org/1999/xlink"
                    viewBox="0 0 1200 120"
                    preserveAspectRatio="none"
                >
                    <defs>
                        <linearGradient id="blendGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="#2a3854" />
                            <stop offset="100%" stopColor="#161f33" />
                        </linearGradient>
                        <filter id="glow-middle">
                            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                            <feMerge>
                                <feMergeNode in="coloredBlur"/>
                                <feMergeNode in="SourceGraphic"/>
                            </feMerge>
                        </filter>
                    </defs>
                    <path
                        d="M0,60 C300,130 600,0 900,60 C1050,90 1150,20 1200,60"
                        fill="none"
                        stroke="url(#blendGradient)"
                        strokeWidth="2.5"
                        filter="url(#glow-middle)"
                    />
                </svg>

                {/* Top curved border - section color */}
                <svg
                    style={{
                        position: "absolute",
                        top: "-80px",
                        left: 0,
                        width: "100%",
                        height: "80px",
                        display: "block",
                    }}
                    xmlns="http://www.w3.org/2000/svg"
                    xmlnsXlink="http://www.w3.org/1999/xlink"
                    viewBox="0 0 1200 120"
                    preserveAspectRatio="none"
                >
                    <defs>
                        <filter id="glow-top">
                            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                            <feMerge>
                                <feMergeNode in="coloredBlur"/>
                                <feMergeNode in="SourceGraphic"/>
                            </feMerge>
                        </filter>
                    </defs>
                    <path
                        d="M0,60 C300,130 600,0 900,60 C1050,90 1150,20 1200,60 L1200,120 L0,120 Z"
                        fill="#161f33"
                    />
                    <path
                        d="M0,60 C300,130 600,0 900,60 C1050,90 1150,20 1200,60"
                        fill="none"
                        stroke="#161f33"
                        strokeWidth="2.5"
                        filter="url(#glow-top)"
                    />
                </svg>

                <StatCard
                    icon={<FaMusic />}
                    value={stats.loading ? "..." : animatedSongs.toLocaleString()}
                    label="Sanger i katalogen"
                    color="#4db193"
                />
                <StatCard
                    icon={<FaList />}
                    value={
                        stats.loading ? "..." : animatedPlaylists.toLocaleString()
                    }
                    label="Spillelister"
                    color="#3a5f8f"
                />
                <StatCard
                    icon={<FaGamepad />}
                    value={
                        stats.loading ? "..." : animatedSessions.toLocaleString()
                    }
                    label="Festlige runder spilt"
                    color="#9d4edd"
                />
            </div>

            {/* Game Preview Section */}
            <div
                style={{
                    padding: "4vh 5vw",
                    backgroundColor: "#161f33",
                    borderBottom: "1px solid #2a3854",
                }}
            >
                <h2
                    style={{
                        fontSize: "clamp(1.5rem, 3vw, 2.5rem)",
                        textAlign: "center",
                        marginBottom: "3vh",
                        color: "#4db193",
                    }}
                >
                    Slik fungerer spillet
                </h2>
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns:
                            "repeat(auto-fit, minmax(280px, 1fr))",
                        gap: "3vw",
                        maxWidth: "1200px",
                        margin: "0 auto",
                    }}
                >
                    <FeatureCard
                        number="1"
                        title="Velg en spilleliste"
                        description="Velg fra vårt store utvalg av spillelister, eller lag din egen med favorittene dine."
                    />
                    <FeatureCard
                        number="2"
                        title="Velg en luke"
                        description="Hvert lag velger en luke med et ord bak. Ordet kan være blått (poengmulighet) eller rødt (turen går videre)."
                    />
                    <FeatureCard
                        number="3"
                        title="Syng og gjett"
                        description="For blå ord: syng en sang med ordet (1 poeng) eller gjett hele sangen (3 poeng). Første lag til 20 poeng vinner!"
                    />
                </div>
            </div>

            {/* Rules Section (Collapsible) */}
            <div
                style={{
                    padding: "6vh 5vw",
                    display: "flex",
                    justifyContent: "flex-start",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                <div
                    style={{
                        backgroundColor: "#161f33",
                        borderRadius: "1vh",
                        padding: "3vh",
                        border: "1px solid #2a3854",
                        maxWidth: "900px",
                        width: "100%",
                    }}
                >
                    <h3
                        style={{
                            fontSize: "1.5rem",
                            marginBottom: "2vh",
                            color: "#4db193",
                            display: "flex",
                            alignItems: "center",
                            gap: "1vh",
                            textAlign: "left",
                        }}
                    >
                        <FaGamepad style={{ fontSize: "1.2rem" }} />
                        Detaljerte regler
                    </h3>
                    <div
                        style={{
                            color: "#d1d5db",
                            lineHeight: 1.8,
                            textAlign: "left",
                        }}
                    >
                        <div style={{ marginBottom: "2vh", textAlign: "left" }}>
                            <strong style={{ color: "white" }}>1. </strong>
                            Deltagerne deles inn i to lag.
                        </div>
                        <div style={{ marginBottom: "2vh", textAlign: "left" }}>
                            <strong style={{ color: "white" }}>2. </strong>
                            Lagene bestemmer seg for lagnavn.
                        </div>
                        <div style={{ marginBottom: "2vh", textAlign: "left" }}>
                            <strong style={{ color: "white" }}>3. </strong>
                            Fasilitator bestemmer hvem som har best lagnavn og
                            dermed får starte.
                        </div>
                        <div style={{ marginBottom: "2vh", textAlign: "left" }}>
                            <strong style={{ color: "white" }}>4. </strong>
                            Første lag velger en luke.
                        </div>
                        <div style={{ marginBottom: "2vh", textAlign: "left" }}>
                            <strong style={{ color: "white" }}>5. </strong>
                            Dersom ordet er rødt går turen rett videre til neste
                            lag som gjentar steg 4. Dersom det er blått, skal
                            laget enten synge på en sang med det ordet i seg (1
                            poeng), eller gjette og synge sangen alle lukene
                            utgjør (3 poeng). Dersom de klarer en av delene, får
                            de gjenta steg 4.
                        </div>
                        <div style={{ marginBottom: "2vh", textAlign: "left" }}>
                            <strong style={{ color: "white" }}>6. </strong>
                            Første lag som når 20 (dette kan endres) poeng
                            vinner!
                        </div>
                        <div
                            style={{
                                marginTop: "3vh",
                                padding: "2vh",
                                backgroundColor: "#0f1624",
                                borderRadius: "0.5vh",
                                border: "1px solid #2a3854",
                                textAlign: "left",
                            }}
                        >
                            <div
                                style={{
                                    marginBottom: "1vh",
                                    color: "#fbbf24",
                                    fontWeight: "bold",
                                    textAlign: "left",
                                }}
                            >
                                ❗️ Dette spillet krever en fasilitator som
                                styrer maskinen og er dommer.
                            </div>
                            <div
                                style={{
                                    fontSize: "0.9rem",
                                    color: "#9ca3af",
                                    textAlign: "left",
                                }}
                            >
                                Bak hver luke er det et ord, og alle lukene
                                utgjør til sammen en setning i en sang.
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Here's How It Works Section */}
            <div
                style={{
                    padding: "2vh 5vw",
                    backgroundColor: "#161f33",
                    borderTop: "1px solid #2a3854",
                    borderBottom: "1px solid #2a3854",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        justifyContent: "flex-start",
                        flexDirection: "column",
                        alignItems: "center",
                    }}
                >
                    <h2
                        style={{
                            fontSize: "clamp(1.5rem, 3vw, 2rem)",
                            marginBottom: "4vh",
                            color: "#4db193",
                            textAlign: "left",
                        }}
                    >
                        Stemningsgaranti
                    </h2>
                    <div
                        style={{
                            width: "60%",
                            maxWidth: "1200px",
                            borderRadius: "1vh",
                            marginBottom: "4vh",
                            overflow: "hidden",
                            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.4)",
                            border: "1px solid #2a3854",
                        }}
                    >
                        <img
                            src="/images/b4b.jpeg"
                            alt="Beat 4 beat game in action"
                            style={{
                                width: "100%",
                                height: "auto",
                                display: "block",
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* Drinking Rules Section */}
            <div
                style={{
                    padding: "6vh 5vw",
                    backgroundColor: "#0f1624",
                    display: "flex",
                    justifyContent: "flex-start",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                <div
                    style={{
                        maxWidth: "900px",
                        width: "100%",
                        backgroundColor: "#161f33",
                        borderRadius: "1vh",
                        padding: "3vh",
                        border: "1px solid #2a3854",
                    }}
                >
                    <h3
                        style={{
                            fontSize: "1.5rem",
                            marginBottom: "2vh",
                            color: "#4db193",
                            display: "flex",
                            alignItems: "center",
                            gap: "1vh",
                            textAlign: "left",
                        }}
                    >
                        🍻 Foreslåtte drikkeregler
                    </h3>
                    <div
                        style={{
                            color: "#d1d5db",
                            lineHeight: 1.8,
                            textAlign: "left",
                        }}
                    >
                        <ul
                            style={{
                                marginLeft: "2vh",
                                listStyleType: "disc",
                                textAlign: "left",
                            }}
                        >
                            <li style={{ marginBottom: "1vh" }}>
                                Drikke tilsvarende antall slurker som nummeret
                                på luken man velger
                            </li>
                            <li style={{ marginBottom: "1vh" }}>
                                Drikke en slurk om man ikke klarer å synge en
                                sang eller får rødt ord
                            </li>
                            <li style={{ marginBottom: "1vh" }}>
                                Del ut en slurk om man får poeng
                            </li>
                            <li style={{ marginBottom: "1vh" }}>
                                Del ut 5 slurker om man gjetter riktig sang
                            </li>
                            <li>Taperlaget må chugge</li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Warning Section */}
            <div
                style={{
                    padding: "8vh 5vw",
                    backgroundColor: "#1f2937",
                    // borderTop: "2px solid #ef4444",
                    textAlign: "center",
                    justifyContent: "center",
                    alignItems: "center",
                    display: "flex",
                }}
            >
                <div
                    style={{
                        backgroundColor: "#161f33",
                        padding: "3vh",
                        border: "1px dashed rgb(207, 200, 0)",
                        width: "50%",
                        borderRadius: "1vh",
                    }}
                >
                    <div
                        style={{
                            fontSize: "1.2rem",
                            color: "#fbbf24",
                            fontWeight: "bold",
                            marginBottom: "1vh",
                        }}
                    >
                        ⚠️ Viktig!
                    </div>
                    <div style={{ color: "#d1d5db", fontSize: "1rem" }}>
                        Skjul skjermen for deltagerne før du går videre til
                        oppsettet
                    </div>
                </div>
            </div>

            {/* CTA Section */}
            <div
                style={{
                    padding: "8vh 5vw",
                    textAlign: "center",
                }}
            >
                <button
                    onClick={() => navigate("/setup")}
                    style={{
                        backgroundColor: "#4db193",
                        color: "white",
                        border: "none",
                        borderRadius: "0.5vh",
                        padding: "2vh 4vw",
                        fontSize: "clamp(1rem, 2vw, 1.3rem)",
                        fontWeight: "bold",
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "1vh",
                        transition: "all 0.3s ease",
                        boxShadow: "0 4px 6px rgba(77, 177, 147, 0.3)",
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#3d9a7a";
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow =
                            "0 6px 12px rgba(77, 177, 147, 0.4)";
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#4db193";
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow =
                            "0 4px 6px rgba(77, 177, 147, 0.3)";
                    }}
                >
                    <FaPlay />
                    Kom i gang
                    <FaArrowRight />
                </button>
            </div>
        </div>
    );
}

function LoadingDots() {
    return (
        <div
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.3rem",
                height: "clamp(2rem, 4vw, 3rem)",
            }}
        >
            <span className="loading-dot" />
            <span className="loading-dot" />
            <span className="loading-dot" />
        </div>
    );
}

function StatCard({ icon, value, label, color }) {
    return (
        <div
            style={{
                backgroundColor: "#161f33",
                borderRadius: "1vh",
                padding: "3vh 2vw",
                minWidth: "200px",
                textAlign: "center",
                border: `2px solid ${color}`,
                boxShadow: `0 4px 12px rgba(0, 0, 0, 0.3)`,
                transition: "transform 0.3s ease",
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-5px)";
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
            }}
        >
            <div
                style={{
                    fontSize: "2.5rem",
                    color: color,
                    marginBottom: "1vh",
                    display: "flex",
                    justifyContent: "center",
                }}
            >
                {icon}
            </div>
            <div
                style={{
                    fontSize: "clamp(2rem, 4vw, 3rem)",
                    fontWeight: "bold",
                    color: "white",
                    marginBottom: "0.5vh",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "clamp(2.5rem, 5vw, 3.8rem)",
                    height: "clamp(2.5rem, 5vw, 3.8rem)",
                }}
            >
                {value === "..." ? <LoadingDots /> : value}
            </div>
            <div style={{ color: "#9ca3af", fontSize: "0.9rem" }}>{label}</div>
        </div>
    );
}

function FeatureCard({ number, title, description }) {
    return (
        <div
            style={{
                backgroundColor: "#0f1624",
                borderRadius: "1vh",
                padding: "2.5vh",
                border: "1px solid #2a3854",
                transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#4db193";
                e.currentTarget.style.transform = "translateY(-3px)";
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#2a3854";
                e.currentTarget.style.transform = "translateY(0)";
            }}
        >
            <div
                style={{
                    fontSize: "2rem",
                    fontWeight: "bold",
                    color: "#4db193",
                    marginBottom: "1vh",
                }}
            >
                {number}
            </div>
            <h3
                style={{
                    fontSize: "1.3rem",
                    marginBottom: "1vh",
                    color: "white",
                }}
            >
                {title}
            </h3>
            <p style={{ color: "#9ca3af", lineHeight: 1.6 }}>{description}</p>
        </div>
    );
}

export default Information;
